import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useChatStream,
  THREAD_KEY,
  PENDING_QUESTION_KEY,
  PENDING_STT_MEETING_KEY,
  markPendingConsumed,
} from "@/hooks/useChatStream";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/useAuth";
import { api, UnauthorizedError } from "@/api/client";
import CustomerSelect, { useMyMailScope } from "@/components/CustomerSelect";
import MessageList from "@/components/MessageList";
import Composer, { loadSavedModelId } from "@/components/Composer";
import Toasts from "@/components/Toasts";
import HistoryDrawer from "@/components/HistoryDrawer";
import BookmarksDrawer from "@/components/BookmarksDrawer";
import ShareDialog from "@/components/ShareDialog";
import HomeWidgets from "@/components/HomeWidgets";
import SuggestedQuestions from "@/components/SuggestedQuestions";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SettingsPanel from "@/components/SettingsPanel";
import SessionGuard from "@/components/SessionGuard";
import { toast } from "@/lib/toast";
import { Menu, LogOut, Plus, Download, Settings as SettingsIcon, Star, Share2, Loader2, AudioLines, Mail, ListChecks } from "lucide-react";

const LAST_CUSTOMER_KEY = "isac.chat.customer";
// 고객사 선택을 저장한 날짜(로컬 YYYY-MM-DD) — 하루가 지나면 새 대화 화면은
// 내부지식으로 초기화한다(어제 선택한 프로젝트 기준으로 오질의하는 실수 방지).
const LAST_CUSTOMER_DATE_KEY = "isac.chat.customer.date";

/** 사용자 기기 기준 오늘 날짜(YYYY-MM-DD). */
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 저장된 고객사 선택이 오늘 저장된 것인지. 날짜 기록이 없으면(과거 버전 저장분) 만료로 본다. */
function isCustomerFresh(): boolean {
  try {
    return localStorage.getItem(LAST_CUSTOMER_DATE_KEY) === todayStr();
  } catch {
    return false;
  }
}

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    messages,
    busy,
    threadId,
    send,
    runSkill,
    stop,
    newThread,
    loadThread,
    confirmExternal,
    setFeedback,
    setBookmark,
    regenerateWithSources,
  } = useChatStream();
  // 양식 문서 작성(Composer '양식으로 작성' 칩)이 질문 없이 호출되면 채울 '직전 답변'의 query_id.
  // 스킬 산출물 턴(downloads 有)은 답변이 아니므로 제외한다.
  const lastAnswerQueryId =
    [...messages].reverse().find((m) => m.role === "bot" && m.sessionId && !(m.downloads?.length))?.sessionId ?? "";
  const [customer, setCustomer] = useState<string>(() => {
    try {
      const v = localStorage.getItem(LAST_CUSTOMER_KEY) ?? "";
      // 직전 대화 복원 대상(THREAD_KEY)이 있으면 대화 연속성을 위해 일단 유지하고,
      // 새 대화 화면으로 시작하는 경우는 하루가 지난 선택을 내부지식으로 초기화.
      if (v && !localStorage.getItem(THREAD_KEY) && !isCustomerFresh()) return "";
      return v;
    } catch {
      return "";
    }
  });
  // 선택한 스코프가 전용(배타) 범위인지 — 안내 문구를 바꾼다. CustomerSelect 와
  // queryKey 가 같아 react-query 캐시를 공유하므로 추가 요청은 발생하지 않는다.
  const { data: customerData } = useQuery({ queryKey: ["customers"], queryFn: api.customers });
  const currentItem = (customerData?.groups ?? [])
    .flatMap((g) => g.items)
    .find((it) => it.value === customer);
  // 내메일 전용 스코프(2026-09-30) — 서버 셀렉터 목록(customers)이 아니라 메일 연동 상태에서 온다.
  const myMail = useMyMailScope();
  const isMyMail = Boolean(myMail) && customer === myMail?.value;
  const exclusiveScope = (Boolean(customer) && Boolean(currentItem?.exclusive)) || isMyMail;
  const exclusiveLabel = isMyMail ? myMail!.label : (currentItem?.label ?? customer);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [seed, setSeed] = useState<{ text: string; nonce: number } | undefined>(undefined);
  // 프로젝트(고객사) 변경 확인 대상값 — 진행 중 대화가 있으면 3-선택지(새 대화/기존 유지/취소) 확인
  const [pendingCustomer, setPendingCustomer] = useState<string | null>(null);
  // 개인 메일 연동 기능 활성 여부 — off(또는 rag-service 불통)면 헤더 링크를 숨긴다.
  const [mailLinkEnabled, setMailLinkEnabled] = useState(false);
  // 메일 업무비서(mail_task) — 기능이 켜져 있을 때만 /tasks 진입 버튼을 보인다.
  const [taskFeature, setTaskFeature] = useState(false);

  // 복원 중 표시 — 직전 대화가 있는 리로드(세션 재인가 포함)에서 홈 화면이
  // '깜빡' 보였다가 대화로 바뀌는 현상을 없앤다(복원이 끝날 때까지 로더 표시).
  const [restoring, setRestoring] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem(THREAD_KEY);
    } catch {
      return false;
    }
  });

  // ── 받아쓰기 → 회의록 작성 자동 실행 ──
  // Stt 페이지에서 "회의록 작성 요청"을 누르면 잡 정보가 sessionStorage 에 보관된 채
  // "/" 로 이동한다. 전문을 내려받아 txt 첨부로 만들어 meeting 스킬을 실행한다.
  const startSttMeeting = async (jobId: string, filename: string) => {
    try {
      const d = await api.sttJob(jobId);
      // 성공적으로 받은 뒤에만 키 제거 — 아래 401(재인가 리로드) 시에는 키를 남겨
      // 리로드 후 이 함수가 다시 실행되며 요청이 유실되지 않는다.
      sessionStorage.removeItem(PENDING_STT_MEETING_KEY);
      if (!d.text?.trim()) {
        toast("받아쓰기 결과가 비어 있어 회의록을 만들 수 없습니다.", "error");
        return;
      }
      const base = filename.replace(/\.[^.]+$/, "").trim() || "받아쓰기";
      const file = new File([d.text], `${base}.txt`, { type: "text/plain" });
      await runSkill([file], customer, "", "meeting", loadSavedModelId());
    } catch (e) {
      if (e instanceof UnauthorizedError) return; // 재인가 리로드 — 키 보존 후 재시도
      try {
        sessionStorage.removeItem(PENDING_STT_MEETING_KEY);
      } catch {
        /* 무시 */
      }
      toast("받아쓰기 내용을 불러오지 못해 회의록 작성을 시작하지 못했습니다.", "error");
    }
  };

  // 새로고침 시 마지막 대화 자동 복원 (서버 이력 기준, 1회)
  // + 세션 재인가 리로드 직전에 보관된 질문(PENDING_QUESTION_KEY) 자동 재전송
  const restoredRef = useRef(false);
  // 복원 응답이 늦게 도착했을 때 덮어쓰기 판단용 — 항상 최신 화면 상태를 가리킨다.
  const liveRef = useRef({ busy, count: messages.length, threadId });
  liveRef.current = { busy, count: messages.length, threadId };
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const tid = threadId;
    (async () => {
      let restored = false; // 이전 대화를 실제로 복원했는지 — 아니면 새 대화 화면이다
      try {
        const d = await api.historyDetail(tid);
        // 복원 응답이 늦게 도착한 사이 사용자가 이미 새 질문/새 대화를 시작했다면
        // 진행 중인 화면을 이전 대화로 덮어쓰지 않는다(검색 직후 '이전 화면으로 돌아가는' 현상 방지).
        if (
          d.turns?.length &&
          !liveRef.current.busy &&
          liveRef.current.count === 0 &&
          liveRef.current.threadId === tid
        ) {
          loadThread(tid, d.turns);
          restored = true;
        }
      } catch {
        /* 이력 없음/오류 — 빈 화면 유지 */
      } finally {
        // 복원 없이 새 대화 화면으로 시작하는데 하루가 지난 고객사 선택이면 내부지식으로 초기화
        if (!restored && customer && !isCustomerFresh()) applyCustomer("");
        setRestoring(false);
        // 세션 재인가(조용한 자동 로그인) 리로드로 유실될 뻔한 질문이 있으면 이어서 재전송
        try {
          const raw = sessionStorage.getItem(PENDING_QUESTION_KEY);
          if (raw) {
            sessionStorage.removeItem(PENDING_QUESTION_KEY);
            const p = JSON.parse(raw) as {
              q?: string;
              customer?: string;
              modelId?: number | null;
              ts?: number;
            };
            if (p?.q && Date.now() - (p.ts ?? 0) < 2 * 60 * 1000) {
              markPendingConsumed(); // 재전송이 다시 401이어도 무한 재인가 루프 금지
              if (p.customer) applyCustomer(p.customer);
              send(p.q, p.customer || customer, p.modelId ?? null);
            }
          }
        } catch {
          /* 무시 */
        }
        // 받아쓰기 페이지에서 넘어온 회의록 작성 요청이 있으면 자동 실행
        try {
          const raw = sessionStorage.getItem(PENDING_STT_MEETING_KEY);
          if (raw) {
            const p = JSON.parse(raw) as { jobId?: string; filename?: string; ts?: number };
            // 5분 경과분은 폐기 — 과거 요청이 뒤늦은 리로드에서 재발동하는 것 방지
            if (p?.jobId && Date.now() - (p.ts ?? 0) < 5 * 60 * 1000) {
              void startSttMeeting(p.jobId, p.filename || "받아쓰기");
            } else {
              sessionStorage.removeItem(PENDING_STT_MEETING_KEY);
            }
          }
        } catch {
          /* 무시 */
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 개인 메일 연동 활성 여부 조회 — 비활성이면 헤더 링크를 노출하지 않는다(회귀 0).
  useEffect(() => {
    let alive = true;
    api
      .mailStatus()
      .then((s) => {
        if (alive) {
          setMailLinkEnabled(!!s.enabled);
          setTaskFeature(!!s.enabled && !!s.task_feature);
        }
      })
      .catch(() => {
        /* 비활성/오류 — 링크 숨김 유지 */
      });
    return () => {
      alive = false;
    };
  }, []);

  // 실제 변경 적용(로컬 저장). 새 대화 전환은 확인 후 별도 처리.
  const applyCustomer = (v: string) => {
    setCustomer(v);
    localStorage.setItem(LAST_CUSTOMER_KEY, v);
    localStorage.setItem(LAST_CUSTOMER_DATE_KEY, todayStr()); // 하루 경과 판정 기준
  };

  // 새 대화 시작 — 하루가 지난 고객사 선택이면 내부지식으로 초기화하고 시작.
  // (앱을 며칠씩 열어두는 경우도 새 대화 진입 시점 기준으로 판정)
  const startNewThread = () => {
    if (customer && !isCustomerFresh()) applyCustomer("");
    newThread();
  };

  const onCustomer = (v: string) => {
    if (v === customer) return;
    // 진행 중 대화가 없으면 바로 변경, 있으면 3-선택지 확인(새 대화/기존 대화 유지/취소).
    if (messages.length === 0) {
      applyCustomer(v);
      return;
    }
    setPendingCustomer(v);
  };

  // 선택 1: 변경하고 새 대화 시작
  const changeWithNewThread = () => {
    if (pendingCustomer === null) return;
    applyCustomer(pendingCustomer);
    newThread();
    setPendingCustomer(null);
  };

  // 선택 2: 변경하고 기존 대화 유지 (이후 질문부터 새 프로젝트 기준으로 검색)
  const changeKeepThread = () => {
    if (pendingCustomer === null) return;
    applyCustomer(pendingCustomer);
    setPendingCustomer(null);
  };

  const onLogout = async () => {
    // 세션 파기 후 "/" 이동 → me()가 public이라 SSO로 자동 재진입하지 않고
    // 로그인 화면이 표시된다(운영포털 로그아웃과 동일 동작).
    try {
      await api.logout();
    } finally {
      window.location.href = "/";
    }
  };

  const handleSelect = async (tid: string) => {
    try {
      const d = await api.historyDetail(tid);
      loadThread(tid, d.turns);
    } catch {
      toast("대화를 불러오지 못했습니다.", "error");
    }
  };

  const exportConversation = () => {
    const lines = messages
      .filter((m) => m.text && !m.streaming)
      .map((m) => (m.role === "user" ? "■ 질문\n" : "▷ 답변\n") + m.text);
    if (lines.length === 0) return;
    const blob = new Blob([lines.join("\n\n────────\n\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ISAC_대화_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareTurns = messages
    .filter((m) => m.text && !m.streaming && !m.error)
    .map((m) => ({ role: m.role, text: m.text }));
  const shareTitle = (messages.find((m) => m.role === "user")?.text ?? "ISAC 공유 대화").slice(0, 60);

  const empty = messages.length === 0;
  const iconBtn =
    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-muted";

  return (
    <div className="flex h-full flex-col">
      <HistoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentThreadId={threadId}
        onSelect={handleSelect}
        onNew={startNewThread}
        onDeletedCurrent={startNewThread}
      />
      <BookmarksDrawer open={bookmarksOpen} onClose={() => setBookmarksOpen(false)} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={shareTitle} turns={shareTurns} />
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <SessionGuard />
      <AnnouncementBanner />

      {pendingCustomer !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPendingCustomer(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 font-semibold">프로젝트 변경</div>
            <p className="text-sm text-muted-foreground">
              진행 중인 대화가 있습니다. 프로젝트를 어떻게 변경할까요?
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={changeWithNewThread}
                className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
              >
                변경하고 새 대화
              </button>
              <button
                onClick={changeKeepThread}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
              >
                변경하고 기존 대화 유지
              </button>
              <button
                onClick={() => setPendingCustomer(null)}
                className="w-full rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상단 안전영역(노치)은 py-2 위에 **더한다**. pt-[env(safe-area-inset-top)] 를
          클래스로 쓰면 py-2 의 padding-top 을 덮어써서, env 가 0 인 데스크톱에서
          padding-top 이 사라지고 헤더 내용(특히 우측 아이콘)이 위로 붙는다.
          같은 수정이 MailLink·Stt·Tasks 헤더와 HistoryDrawer 에도 들어가 있다. */}
      <header className="sticky top-0 z-10 flex items-center gap-1 border-b border-border bg-background/90 px-2 py-2 backdrop-blur" style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}>
        <button onClick={() => setDrawerOpen(true)} className={iconBtn} aria-label="대화 이력" title="대화 이력">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex shrink-0 items-center gap-1.5 font-bold">
          <img src="/icon-512.png" alt="ISAC" className="h-6 w-6 rounded-md" />
          <span className="text-[#EB4E26]">ISAC</span>
        </div>
        {/* 로고와 범위 선택 버튼 사이를 2cm 로 유지한다.
            header 의 gap-1(0.25rem)이 이미 더해지므로 그만큼 빼서 실측 2cm 을 맞춘다.
            Tailwind 임의값(ml-[calc(2cm_-_0.25rem)]) 대신 인라인 style 을 쓰는 이유:
            물리 단위 + calc 조합은 JIT 파싱 규칙(공백→_)에 걸리기 쉬워, 빌드에서
            클래스가 조용히 누락되면 간격이 사라진 채 배포된다. */}
        <div className="min-w-0 flex-1" style={{ marginLeft: "calc(2cm - 0.25rem)" }}>
          <CustomerSelect value={customer} onChange={onCustomer} />
        </div>
        {/* 받아쓰기(다글로 STT) — 음성 파일 → 텍스트 변환 페이지 */}
        <button
          onClick={() => navigate("/stt")}
          className={iconBtn}
          aria-label="받아쓰기"
          title="받아쓰기 — 음성을 텍스트로"
        >
          <AudioLines className="h-5 w-5" />
        </button>
        {/* 개인 메일 연동 — 기능이 켜져 있을 때만 노출 */}
        {mailLinkEnabled && (
          <button
            onClick={() => navigate("/mail")}
            className={iconBtn}
            aria-label="내 메일 연동"
            title="내 메일 연동 — 내 회사 메일을 나만 검색하도록 저장"
          >
            <Mail className="h-5 w-5" />
          </button>
        )}
        {/* 메일 업무비서 — 내 메일에서 뽑은 할 일·기한 (기능 on 일 때만) */}
        {taskFeature && (
          <button
            onClick={() => navigate("/tasks")}
            className={iconBtn}
            aria-label="내 업무"
            title="내 업무 — 메일에서 추출한 할 일·기한"
          >
            <ListChecks className="h-5 w-5" />
          </button>
        )}
        <button
          onClick={() => setShareOpen(true)}
          disabled={empty}
          className={iconBtn + " disabled:opacity-30"}
          aria-label="대화 공유 링크"
          title="읽기전용 공유 링크 만들기"
        >
          <Share2 className="h-5 w-5" />
        </button>
        <button
          onClick={() => setBookmarksOpen(true)}
          className={iconBtn}
          aria-label="즐겨찾기"
          title="즐겨찾기"
        >
          <Star className="h-5 w-5" />
        </button>
        <button
          onClick={exportConversation}
          disabled={empty}
          className={iconBtn + " disabled:opacity-30"}
          aria-label="대화 내보내기"
          title="대화 내보내기(txt)"
        >
          <Download className="h-5 w-5" />
        </button>
        <button onClick={startNewThread} className={iconBtn} aria-label="새 대화" title="새 대화">
          <Plus className="h-5 w-5" />
        </button>
        <button onClick={() => setSettingsOpen(true)} className={iconBtn} aria-label="설정" title="설정(글자 크기·고대비·알림)">
          <SettingsIcon className="h-5 w-5" />
        </button>
        <button onClick={onLogout} className={iconBtn} aria-label="로그아웃" title={user?.email ?? "로그아웃"}>
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        {empty ? (
          restoring ? (
            /* 직전 대화 복원 중 — 홈 화면 깜빡임 대신 로더 표시 */
            <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> 대화 불러오는 중…
            </div>
          ) : (
          <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center gap-5 px-6 text-center">
            <img src="/icon-512.png" alt="ISAC" className="h-16 w-16 rounded-2xl" />
            <div>
              <h2 className="text-lg font-bold">무엇을 도와드릴까요?</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {isMyMail
                  ? `'${exclusiveLabel}' — 연동한 내 메일에서만 검색합니다. 지식저장소 자료는 참조하지 않습니다.`
                  : exclusiveScope
                  ? `'${exclusiveLabel}' 범위에서만 검색합니다. 사내 공통자료는 참조하지 않습니다.`
                  : customer
                    ? `'${customer}' 기준으로 답변합니다.`
                    : "고객사를 선택하면 더 정확해요."}
              </p>
            </div>
            {/* 추천 질문도 입력창과 같은 선택 모델로 (2026-09-30) */}
            <SuggestedQuestions onPick={(q) => send(q, customer, loadSavedModelId())} disabled={busy} />
            <HomeWidgets />
            <ul className="w-full max-w-md space-y-1.5 text-left text-sm text-muted-foreground">
              <li>· 상단에서 <b className="text-foreground">고객사</b>를 선택하고 질문하세요.</li>
              <li>· 파일을 <b className="text-foreground">첨부</b>(여러 개·드래그앤드롭)하면 <b className="text-foreground">회의록·요약·번역·비교</b>로 만들 수 있어요.</li>
              <li>· 상단 🎙 <b className="text-foreground">받아쓰기</b>로 녹음 파일을 텍스트로 변환할 수 있어요.</li>
              <li>· 답변의 <b className="text-foreground">출처</b>를 눌러 원문을 확인하고, 👍/👎·<b className="text-foreground">즐겨찾기 ⭐</b>로 표시해 두세요.</li>
              <li>· 상단 <b className="text-foreground">공유</b>로 읽기전용 링크를 만들어 동료에게 전달할 수 있어요.</li>
              <li>· <b className="text-foreground">설정 ⚙</b>에서 답변 분량·말투·영어 답변을 조절할 수 있어요.</li>
              <li>· 왼쪽 위 <b className="text-foreground">☰</b>에서 지난 대화를 다시 열 수 있어요.</li>
            </ul>
          </div>
          )
        ) : (
          <MessageList
            messages={messages}
            customer={customer}
            onConfirm={confirmExternal}
            onFeedback={setFeedback}
            onEdit={(t) => setSeed({ text: t, nonce: Date.now() })}
            // 다시 답변·참고문서 제외 재생성·후속 질문 제안도 선택 모델을 보낸다 (2026-09-30, 누락 수정)
            onRegenerate={(q) => send(q, customer, loadSavedModelId())}
            onRegenerateSources={(q, urls, cust) =>
              regenerateWithSources(q, cust ?? customer, urls, loadSavedModelId())}
            onSuggest={(q) => send(q, customer, loadSavedModelId())}
            onBookmark={setBookmark}
            onDocFill={(m, file) =>
              runSkill([file], customer, "", "doc_fill", loadSavedModelId(), false, m.sessionId ?? "")}
          />
        )}
      </main>

      <Composer
        busy={busy}
        onSend={(t, modelId) => send(t, customer, modelId ?? null)}
        onStop={stop}
        onSkillSend={(files, t, hint, modelId, hwocr) =>
          runSkill(files, customer, t, hint, modelId ?? null, hwocr ?? false, lastAnswerQueryId)}
        seed={seed}
      />
      <Toasts />
    </div>
  );
}
